<template>
	<div class="news-list">
		<GlobalPagination v-model="currentPage" :maxPage="maxPage" asParam class="news-list__pagination" />
		<Transition name="fade" mode="out-in">
			<div v-if="loader" class="news-list__loader">
				<v-progress-circular indeterminate color="black" :size="48"></v-progress-circular>
			</div>
			<div v-else class="news-list__elements">
				<NewsCard v-for="post in fetchedNews" :key="post.id" :post="post" />
			</div>
		</Transition>

		<div class="news-list__bottom-pagination">
			<nuxt-link
				:to="isFirstPage ? undefined : createURL(currentPage - 1)"
				class="news-list__pagination-arrow news-list__pagination-arrow--previous"
				:class="{ 'news-list__pagination-arrow--previous-disabled': isFirstPage }">
				<GlobalIcon name="arrow-left" :height="16" />
			</nuxt-link>
			<div
				v-for="page in paginationPages(currentPage, maxPage)"
				:key="page.pageNumber"
				class="news-list__pagination-page"
				:class="{ 'news-list__pagination-page--current': page.isCurrent }">
				<nuxt-link v-if="!page.isDots && !page.isOf" :to="createURL(page.pageNumber)">
					{{ page.pageNumber }}
				</nuxt-link>
				<span v-else-if="page.isDots">...</span>
				<span v-else>{{ $t('common.of') }}</span>
			</div>
			<nuxt-link
				:to="isLastPage ? undefined : createURL(currentPage + 1)"
				class="news-list__pagination-arrow news-list__pagination-arrow--next"
				:class="{ 'news-list__pagination-arrow--next-disabled': isLastPage }">
				<GlobalIcon name="arrow-right" :height="16" />
			</nuxt-link>
		</div>
		<!-- <GlobalPagination v-model="currentPage" :maxPage="maxPage" class="news-list__pagination" /> -->
	</div>
</template>

<script setup lang="ts">
const i18nService = usei18nService()
const fetchedNews = ref<Partial<INewsModel>[]>()
const localeLang = i18nService.getLocaleLang()
const loader = ref(false)
const currentPage = ref(1)
const maxPage = ref(1)
// const router = useRouter()
const route = useRoute()
const localePath = useLocalePath()

// currentPage.value = router.currentRoute.value.query.page ? +router.currentRoute.value.query.page : 1
currentPage.value = route.params.page ? +route.params.page : 1

const fetchNews = async () => {
	loader.value = true
	try {
		const { data, meta } = await ApiService.fetchNews(currentPage.value, 12, '', localeLang)

		// if (data?.length === 0) {
		// 	throw createError({ statusCode: 404 })
		// }

		if (meta) maxPage.value = meta.last_page ? meta.last_page : meta.lastPage
		if (data) fetchedNews.value = data
	} catch (error) {
		console.log(error)
	}
	loader.value = false
}

await fetchNews().then(() => {
	if (!fetchedNews.value || fetchedNews.value.length === 0) {
		throw createError({ statusCode: 404 })
	}
})

interface IPaginationPage {
	isDots?: boolean
	isOf?: boolean
	isCurrent?: boolean
	pageNumber: number
}

const paginationPages = (currentPage: number, maxPages: number) => {
	const maxPagesToShow = 5
	const pages: IPaginationPage[] = []
	let startPage = 1
	let endPage = maxPages

	if (maxPages > maxPagesToShow) {
		const middle = Math.ceil(maxPagesToShow / 2)
		startPage = currentPage - middle + 1
		endPage = currentPage + middle - 1

		if (startPage < 1) {
			startPage = 1
			endPage = maxPagesToShow
		} else if (endPage > maxPages) {
			startPage = maxPages - maxPagesToShow + 1
			endPage = maxPages
		}
	}

	if (startPage > 1) {
		pages.push({ isDots: false, isCurrent: false, pageNumber: 1 })
		if (startPage > 2) pages.push({ isDots: true, isCurrent: false, pageNumber: 0 })
	}

	for (let i = startPage; i <= endPage; i++) {
		pages.push({ isDots: false, isCurrent: i === currentPage, pageNumber: i })
	}

	if (endPage < maxPages) {
		pages.push({ isOf: true, isCurrent: false, pageNumber: 0 })
		pages.push({ isDots: false, isCurrent: false, pageNumber: maxPages })
	}

	return pages
}

const createURL = (page: number) => {
	return localePath({ name: 'news-page', params: { page: page > 1 ? page : null } })
}

// const handleNextPageClick = () => {
// 	if (currentPage.value <= maxPage.value) {
// 		currentPage.value += 1
// 		// router.push(localePath({ name: 'news-page', params: { page: currentPage.value } }))
// 		window.scrollTo(0, 0)
// 	}
// }

// const handlePreviousPageClick = () => {
// 	if (currentPage.value > 1 && currentPage.value <= maxPage.value) {
// 		currentPage.value -= 1
// 		// router.push(localePath({ name: 'news-page', params: { page: currentPage.value } }))
// 		window.scrollTo(0, 0)
// 	}
// }

// const handleNumberPageClick = (pageNumber: number) => {
// 	currentPage.value = pageNumber
// 	// router.push(localePath({ name: 'news-page', params: { page: currentPage.value } }))
// 	window.scrollTo(0, 0)
// }

const isLastPage = computed(() => currentPage.value === maxPage.value)
const isFirstPage = computed(() => currentPage.value === 1)

watch(
	() => currentPage.value,
	(newValue) => {
		if (newValue) fetchNews()
	},
)
</script>

<style lang="scss">
@import './PageList.scss';
</style>
